import { NgpDialogRef } from 'ng-primitives/dialog';

import {
  DialogCloseEvent,
  DialogCloseReason,
  DialogCloseOptions,
} from './dialog-result';

import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * SUI wrapper around NgpDialogRef with clean, flat, typed close events.
 *
 * * Flattens ng-primitives close output into DialogCloseEvent
 * * Detects implicit closes (ESC / backdrop) and assigns proper reasons
 * * Provides typed data input/output
 *
 * @template TData    Data passed TO the dialog (config.data)
 * @template TResult  Data returned FROM the dialog (via close options)
 *
 * @example Opening a dialog
 * const dialogRef = dialogService.openTabsDialog<MyComponent, MyOutput>(
 *   MyComponent,
 *   { data: { userId: 123 } }
 * );
 *
 * @example Subscribing to results
 * dialogRef.closed.subscribe(event => {
 *   if (event.reason === DialogCloseReason.Save) {
 *     console.log('Saved:', event.data);
 *   }
 * });
 *
 * @example Inside the dialog component
 * this.dialogRef.close({
 *   reason: DialogCloseReason.Save,
 *   data: formData
 * });
 */
export class SuiDialogRef<TData = unknown, TResult = unknown> {
  /**
   * * Access raw ng-primitives ref for special cases.
   * * Mainly used for flags like disableClose in dirty handlers.
   * ! Avoid using directly unless necessary.
   */
  readonly ngpRef: NgpDialogRef<TData, any>;

  get data(): TData | undefined {
    return this.ngpRef.data;
  }

  /** Stream of standardized SUI dialog close events */
  readonly closed: Observable<DialogCloseEvent<TResult>>;

  constructor(ngpRef: NgpDialogRef<TData, any>) {
    this.ngpRef = ngpRef;

    // * Transform nested ngpRef structure into flat SUI event
    this.closed = this.ngpRef.closed.pipe(
      map((event) => this.transformCloseEvent(event)),
    );
  }

  /** * Closes the dialog with standardized options */
  close(options: DialogCloseOptions<TResult>): void {
    this.ngpRef.close(options);
  }

  /**
   * * Converts ng-primitives event -> flat DialogCloseEvent.
   * Handles:
   * 1. Our explicit close({ reason, data })
   * 2. Implicit closes (ESC / backdrop) with result undefined
   * 3. Unknown patterns -> fallback to Cancel
   */
  private transformCloseEvent(event: {
    focusOrigin?: any;
    result?: any;
  }): DialogCloseEvent<TResult> {
    const { focusOrigin, result } = event;

    // * Scenario 1: Explicit close using DialogCloseOptions
    if (result && typeof result === 'object' && 'reason' in result) {
      return {
        reason: result.reason as DialogCloseReason,
        data: result.data,
        focusOrigin,
      };
    }

    // * Scenario 2: Implicit closes from ng-primitives (ESC/backdrop)
    // ! result = undefined -> determine reason from focusOrigin
    if (result === undefined) {
      const reason =
        focusOrigin === 'keyboard'
          ? DialogCloseReason.Escape
          : focusOrigin === 'mouse'
            ? DialogCloseReason.Backdrop
            : DialogCloseReason.Cancel;

      return { reason, focusOrigin };
    }

    // * Scenario 3: Fallback for unknown or legacy patterns
    // ! Treat as Cancel but keep whatever data was returned
    return {
      reason: DialogCloseReason.Cancel,
      data: result,
      focusOrigin,
    };
  }
}
